
import { NoseurObject } from "@rivtn/noseur";
import { IAdministrator } from "./AdministrationService";
import { BaseService, SanitizedResponse } from "../../BaseService";
import { Status } from "../../../utils";

export interface ICrmLead {
    id: number;
    status: Status;
    created_at: string;
    updated_at: string;
    external_id: string;
    company_name: string;
    contact_name: string;
    contact_email: string;
    contact_phone: string;
    potential_value: number;
    next_follow_up_date: string;
    extra_data: NoseurObject<any>;
    administrator: IAdministrator;
}

export interface ICrmLeadNote {
    id: number;
    note: string;
    status: Status;
    created_at: string;
    external_id: string;
    administrator: IAdministrator;
}

export class PipelineService extends BaseService {

    protected static instance: PipelineService;
    static STATUS_COLOR_MAP = { NEW: "#618CF1", MEETING: "#B3C322", CONTACTED: "#D4319E", CLOSED_WON: "#0FA883", NEGOTIATION: "#3C31D4", CLOSED_LOST: "#EB5959", CLOSED_INDECISIVE: "#000000", };

    private constructor() {
        super();
    }

    static getInstance() {
        if (!PipelineService.instance) PipelineService.instance = new PipelineService();
        return PipelineService.instance;
    }

    // Crm Leads

    queryCrmLeadsStat(params: NoseurObject<string | number> = {}): Promise<any> {
        return this.report(this.transport.get("/janus/geminus/api/v1/crm/lead/stat", { query: this.normalizeParams(params) }));
    }

    queryCrmLeads(params: NoseurObject<string | number> = { page: 1, size: 100 }): Promise<any> {
        return this.report(this.transport.get("/janus/geminus/api/v1/crm/lead", { query: this.normalizeParams(params) }));
    }

    getSingleCrmLead(externalId: string): Promise<any> {
        return this.report(this.transport.get(`/janus/geminus/api/v1/crm/lead/${externalId!}`, this.config));
    }

    createCrmLead(payload: any): Promise<any> {
        return this.report(this.transport.post(`/janus/geminus/api/v1/crm/lead`, payload, this.config));
    }

    updateCrmLead(payload: any): Promise<any> {
        return this.report(this.transport.patch(`/janus/geminus/api/v1/crm/lead/${payload.external_id}`, payload, this.config));
    }

    queryCrmLeadsNotes(crmLeadId: string): Promise<any> {
        return this.report(this.transport.get("/janus/geminus/api/v1/crm/lead/note", { query: { "crm_lead.external_id": crmLeadId } }));
    }

    updateCrmLeadStage(crmLeadId: string, payload: any): Promise<any> {
        return this.report(this.transport.put(`/janus/geminus/api/v1/crm/lead/${crmLeadId}/stage`, payload));
    }

    queryCrmLeadsAnalytics(params: NoseurObject<string | number> = { page: 1, size: 10 }, monetaryValue = false): Promise<any> {
        const { supplementParams, graphDataKey, labelKey } = this.getIdealStatQueryForDateRange(params.from as string, params.to as string);
        return this.report(this.transport.get(`/janus/geminus/api/v1/crm/lead/stat`, { query: this.normalizeParams({ ...params, ...supplementParams }) }), (response: SanitizedResponse<any>) => {
            const graphData = response.sanitized[graphDataKey];
            response.apiResponse.data = {};
            response.apiResponse.data.labels = graphData?.map((g: any) => g[labelKey]);
            if (monetaryValue) {
                response.apiResponse.data.datasets = Object.entries(PipelineService.STATUS_COLOR_MAP).map(([label, backgroundColor]) => ({
                    label,
                    backgroundColor,
                    data: graphData?.map((g: any) => {
                        const value = parseFloat("" + ((g["potential_value_sum"] * g["top_status_percentage"][label]) / 100)) / 1000000;
                        return isNaN(value) ? 0 : value;
                    })
                }));
            } else {
                response.apiResponse.data.datasets = Object.entries(PipelineService.STATUS_COLOR_MAP).map(([label, backgroundColor]) => ({
                    label,
                    backgroundColor,
                    data: graphData?.map((g: any) => (g["top_status_count"][label] ?? 0))
                }));
            }
        });
    }

}
